import { Link } from "react-router-dom";
import { Mail, Heart } from "lucide-react";
import { Header } from "@/App";
import CredibilityStrip from "../components/CredibilityStrip";
import TempleQuote from "../components/TempleQuote";

export default function About() {
  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="about-page">
      <Header />

      {/* THE ARRIVAL */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="section-arrival">The Arrival</p>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tighter text-[#042F2E] leading-[1.05] mb-8" data-testid="about-headline">
            I was born intuitive.<br />
            <span className="italic text-[#25a1c2]">Adopted at birth.</span>
          </h1>

          {/* Credibility Strip — E-E-A-T proof */}
          <div className="mb-10 pb-8 border-b border-[#CCFBF1]">
            <CredibilityStrip variant="light" />
          </div>

          <div className="space-y-5 text-[#068fa2] leading-relaxed text-[18px]">
            <p>
              By the time I was a young girl I was already hearing, feeling, sensing people who had crossed over. Animals who had crossed over. Sometimes the thoughts of the living. There was no elder to hold and teach me. So I shut down that aspect of me — and would later spend over twenty years researching Mind, Body, Spirit, looking for the language no one had given me.
            </p>
            <p className="font-serif italic text-[#042F2E] text-xl">
              I spent fifty years searching for a home within myself.
            </p>
            <p>Then I called a genealogist.</p>
            <p>
              She sent me a fan diagram. I sat down at my desk with it open on my screen, and I looked. And I looked. <em>O-M-G!</em> I smiled. I was eager. Excited. I digested.
            </p>
            <p>
              I had never seen a family tree with my name on it. I had never had this depth — this lineage, this weight of people behind me. As a little girl in school, when teachers asked the class to bring in family trees, I felt so embarrassed. I didn't fit. I didn't know. And — I was scared to bring it up to my parents, for fear of hurting them.
            </p>
            <p className="font-serif italic text-[#042F2E] text-xl">
              That afternoon, the sadness lifted.
            </p>
            <p>
              That afternoon, I met my ancestors. Scottish clans from <strong>Edinburgh Castle</strong>. Spanish lineage. <strong>Civil War veterans. WWI veterans. WWII veterans.</strong> My <strong>6th great-grandfather, Benjamin Franklin</strong> — where the creator and the inventor in me had been quietly living all along, waiting to be claimed.
            </p>
            <p>
              I kept the tree on my screen. Seven years later, it is still there. I say hello to them every morning. I send them my gratitude. And now, when I watch a movie or hear someone speak of ancestry, WWII, the Civil War — I feel it in my body. I claim it. <strong>It is a knowing.</strong>
            </p>
            <p className="font-serif italic text-[#042F2E] text-xl pt-2">
              That was one of the times I came home.
            </p>
          </div>
        </div>
      </section>

      {/* THE HOMECOMING */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="section-homecoming">The Homecoming — My Soul's Journey</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#042F2E] mb-8 leading-tight">
            I have come home many times in this life.
          </h2>

          <div className="space-y-5 text-[#068fa2] leading-relaxed text-[17px]">
            <p>
              I came home the day I was reunited with — and called — my biological mother. After my intuition screamed at me for months while I carried my third daughter. I sent for my birth records. I wrote a careful letter. And on the very day I came home from giving birth, a letter arrived from Florida.
            </p>
            <p>It started:</p>
            <p className="bg-[#F0FDFA] border-l-4 border-[#5EEAD4] rounded-r-lg px-5 py-4 font-serif italic text-[#042F2E] text-lg">
              "You have never been a secret. And I have loved you from the moment you were born."
            </p>
            <p>
              I called her. We talked for hours. I asked about her voice — why it sounded hoarse — and she told me she was fighting cancer for the second time. Within two months, I was on a plane with my newborn in my arms, flying to meet her, my three half-siblings, and a whole second family.
            </p>
            <p>
              She crossed over six months later. What a full-circle moment and blessing for her and me to be able to spend the precious time together that we did.
            </p>
            <p>
              I reunited with her on the day I birthed my third daughter. I am her third child. She was the third daughter in her own line. <strong>The threes repeated. My intuition had known. My Divine Team had known.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* THE NAME */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="section-name">The Name</p>
          <div className="space-y-5 text-[#068fa2] leading-relaxed text-[17px]">
            <p>
              I chose the name <strong>Temple Franklin</strong> years later, after the genealogist gave me my lineage back. Every other name I had carried in this life had been given to me by someone else, and had served its purpose. <em>Temple</em> is in my lineage and means home — coming home to yourself, home within yourself. <em>Franklin</em> is also in my ancestral line, the inventor's blood. These are the first names I have ever carried that are <strong>mine.</strong> Anchored. Earned. Remembered.
            </p>
            <p>
              I spent years looking for the Mind, Body, Spirit teachers who could meet me where I lived.
            </p>
            <p>
              I am a natural-born intuitive and evidential medium. I am <strong>HeartMath certified</strong> and trained in multiple energy modalities — <strong>Reiki, The Reconnection, Practical Intuition,</strong> and more. My evidential mediumship lineage came through the real ones: <strong>Sonia Choquette, Carmel Joy Baird, James Van Praagh, John Edward, Ainslie MacLeod, Wayne Dyer,</strong> and <strong>Louise Hay.</strong> I traveled the world for three years studying with a renowned meditation teacher — sitting in four-hour pineal-gland meditations, in healing pods, in workshops where I watched plasma cloaks of light drape themselves over the people I was holding space for.
            </p>
            <p>
              I learned every one of them not to build a résumé, but <strong>to bring myself home.</strong> To rediscover what it authentically felt like to live in my own skin. To feel my own soul. To know what authentic love feels like when it is not being performed.
            </p>
            <p>
              I have done client work as an evidential medium — readings, holding remote space for families with loved ones in comas, holding space for the dying as they crossed. <em>All in divine white light, in the purest form possible.</em>
            </p>
            <p>
              For more than twenty years, I have held space for women — <strong>on radio, on the road, and on the other side of grief.</strong>
            </p>
            <p>
              I built my own radio show, divinely guided. I was so scared — to speak in public, to read in public, to speak my truth. <em>What was my truth, anyway?</em> I surrendered. That show evolved into a <strong>five-host network I ran for three years.</strong> Cumulatively, I conducted my own <strong>international radio show for 15 years</strong> (KKNW Seattle, CBS Radio East Coast) — with a three-year pause to walk my youngest daughter through concussion recovery.
            </p>
            <p>
              For just under three years in my late 40s, I traveled the world holding space — as <strong>Team Leader and Activity Leader</strong> — inside seven-day intensive immersion workshops, with thousands of women overcoming their fears and limiting beliefs. I trained hundreds of volunteers and team leaders.
            </p>
          </div>
        </div>
      </section>

      {/* TO THE STUDS */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="section-studs">To the Studs</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-8 leading-tight">
            Then life brought me down to the studs.
          </h2>
          <div className="space-y-5 text-[#068fa2] leading-relaxed text-[17px]">
            <p>
              Divorce after twenty-two years. The world came to a halt. My business — gone. My adoptive mother's hidden cancer — then weaponized by the people who were supposed to be "family." All three of my daughters, now estranged. Peri-menopause. Menopause. Hashimoto's. A breast cancer scare. All following my adoptive mother's death and the deep grief and trauma that came with it. <strong className="text-[#042F2E]">The body always knows.</strong>
            </p>
            <p className="font-serif italic text-[#042F2E] text-2xl pt-2">
              For seven years I was shattered.
            </p>
            <p>
              I went looking for answers and found a functional-medicine path: full blood panels, a naturopath who listened, real testing, real support. The traditional doctors meant well — they simply did not know what they did not know. I learned to advocate for my own body.
            </p>
          </div>
        </div>
      </section>

      {/* THE REBUILD AND THE AWARENESS */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="section-rebuild">The Rebuild and the Awareness</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-8 leading-tight">
            I rebuilt slowly. <span className="italic text-[#25a1c2]">Daily. Minute by minute.</span>
          </h2>
          <div className="space-y-5 text-[#068fa2] leading-relaxed text-[17px]">
            <p>
              Every morning I wake up already walking with my Divine Team. I say <em>hello.</em> I say <em>thank you.</em> I say <em>love you too.</em> They walk with me to coffee. They bless my morning routine.
            </p>
            <p>
              In meditation, I lay down and call upon Mother Gaia as she surrounds me in her <strong>love nest.</strong> <strong>Archangel Michael</strong> stands at my upper left. The archangels arch to the right like a rainbow, landing with <strong>Metatron.</strong> <strong>Goddess Isis</strong> sits above my right shoulder. <strong>The Healers</strong> — three tall light beings — are always to my left.
            </p>
            <p>
              I walk every day with my dog <strong>Levi.</strong> My dad loved Blue Herons when he was alive — so when I see a Blue Heron, it is a sign from him. Then one day I saw a White Heron, and I took that as a sign from my mom. All the signs come in <em>divine timing</em> — I do not see them every walk. When I do, I say <em>Hello Mom. Hello Dad. Thank you for the sign.</em>
            </p>
            <p>
              There are days I ask the divine for heart-shaped rocks. There is a garden full of them now.
            </p>

            {/* HEART ROCKS PHOTO */}
            <figure className="pt-4" data-testid="heart-rocks-figure">
              <img
                src="/images/heart-rocks.jpg"
                alt="Temple's hand holding two heart-shaped rocks gathered in her Arizona garden"
                className="w-full rounded-2xl shadow-lg border border-[#CCFBF1]"
                loading="lazy"
                data-testid="heart-rocks-image"
              />
              <figcaption className="font-serif italic text-center text-[#068fa2] text-base mt-3">
                There is a garden full of them now.
              </figcaption>
            </figure>

            <p className="pt-2">
              Every meal: thank you, Mother Gaia. Thank you, the chickens. Thank you, the farmers.
            </p>
            <p className="font-serif italic text-[#042F2E] text-xl pt-2">
              This is the Mind Body Spirit HYGIENE™ Method in play.
            </p>
          </div>
        </div>
      </section>

      {/* THE PILGRIMAGE HOME */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="section-pilgrimage">The Pilgrimage Home</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#042F2E] mb-8 leading-tight">
            Just shy of Mother's Day, four years ago, my adoptive mother died.
          </h2>
          <div className="space-y-5 text-[#068fa2] leading-relaxed text-[17px]">
            <p>
              I was living in Bend, Oregon — divinely guided to move there from over forty years in Washington state, where I had built community and family. I was terrified to move away, so I asked Spirit for a sign that I could not deny. A deer jumped out of the trees in front of me. A grasshopper landed on my car door handle when I returned from my run. There were others, and I looked them all up for clarity and validation. I moved to Bend, Oregon, alone. Scared, and doing it anyway. I surrendered to the greater plan that the Divine had in store for me. <em>The messy middle!</em>
            </p>
            <p>
              Fast-forward a couple of years. I am in my Bend home. My mother — who raised me — has, unbeknownst to me, been battling cancer for three years. She is now in hospice. <strong>And she denies us a bedside visit — herself, and me. She denied herself the closure as much as she denied me.</strong>
            </p>
            <p>
              She crosses just after Mother's Day. I have to put my oldest dog down for health reasons. My Bend home is now rented out. I packed up for a road trip.
            </p>
            <p>
              I got into my Sprinter van with Levi and started my cathartic drive to find my home (again).
            </p>
            <p>
              Bend, Oregon to a very special place — <strong>Haystack Rock</strong> on the Oregon Coast. I stayed there for a few days, walking the beach, crying, talking to her — hurt, angry, in disbelief, grieving. Reminiscing. Through Oregon. Through California. Into Arizona. The whole drive, I could sense my parents were on another road trip with me — like what we would do together when I was a little girl. At one point I think it was Dad who said, <em>take this exit.</em> So I did. A new adventure. I just let Dad take the wheel. Eventually I ended up back on the freeway. The ride he took us on had beautiful scenery and was well worth the detour. <em>Thank you, Dad.</em>
            </p>
            <p>
              I arrived in <strong>Arizona</strong> — the place I was born, the place I was adopted from. I eventually sold the Bend, Oregon home, and now I am living back in Arizona.
            </p>
            <p>
              I had been carrying the manuscript for years. I knew everything was in there. I just couldn't name it.
            </p>
            <p>
              And then, in Arizona — <em>the homecoming</em> — the divine handed me three words I couldn't have written on my own:
            </p>
            <p className="font-serif text-5xl md:text-6xl text-[#042F2E] tracking-tighter text-center py-4" data-testid="i-get-to-headline">
              I GET TO.
            </p>
            <p className="font-serif italic text-[#25a1c2] text-xl text-center">
              I get to grieve. I get to rebuild. I get to be guided.
            </p>
          </div>
        </div>
      </section>

      {/* THE INVITATION */}
      <section className="pb-12 px-6 pt-16">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="section-invitation">The Invitation</p>
          <h2 className="font-serif text-5xl md:text-7xl tracking-tighter text-[#042F2E] leading-[0.95] mb-3">
            This is for the woman<br />
            <span className="italic text-[#25a1c2]">who has said NO.</span>
          </h2>
          <p className="font-serif italic text-2xl text-[#068fa2] mb-10">
            The buck stops here.
          </p>

          <p className="text-[#068fa2] text-[17px] leading-relaxed mb-8">
            And now — because she said no — she is finally ready to ask:
          </p>

          <div className="space-y-5 text-[#042F2E] font-serif italic text-xl md:text-2xl leading-relaxed border-l-4 border-[#5EEAD4] pl-6">
            <p data-testid="invitation-q1">Is this what my life is supposed to feel like?</p>
            <p data-testid="invitation-q2">Have I ever actually felt love — or have I just performed it beautifully?</p>
            <p data-testid="invitation-q3">What does my GOD / DIVINE / SPIRIT feel and look like, authentically, to me?</p>
            <p data-testid="invitation-q4">Who am I, really? Authentically me — what does that even look like?</p>
            <p data-testid="invitation-q5">I have been shattered. What the hell do I do now?</p>
            <p data-testid="invitation-q6">What if the next chapter is not the one that was handed to me — but the one I finally write myself? <span className="not-italic">How?</span></p>
          </div>
        </div>
      </section>

      {/* TEMPLE QUOTE — Cut the word TRY (tactical reframe inside The Invitation arc) */}
      <TempleQuote
        quote="Cut the word TRY from your vocabulary."
        variant="hero"
        theme="cream"
        id="quote-cut-try-about"
      />

      {/* TEMPLE QUOTE — Mission anchor */}
      <TempleQuote
        quote="For every woman who picks up this work, an entire family system shifts."
        variant="hero"
        theme="cream"
        id="quote-family-shifts-about"
      />

      {/* CTA / WELCOME HOME */}
      <section className="pb-20 px-6 pt-12">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#042F2E] via-[#068fa2] to-[#25a1c2] rounded-3xl p-10 md:p-14 text-white">
          <p className="font-serif italic text-[#5EEAD4] text-xl mb-3">If any of those questions just landed in your body —</p>
          <h3 className="font-serif text-4xl md:text-5xl tracking-tight mb-6">
            Welcome home.
          </h3>
          <p className="text-[#5EEAD4] italic text-lg mb-6">
            Welcome to your next journey of authentic self-discovery.
          </p>

          <div className="space-y-4 text-white/90 leading-relaxed text-[17px]">
            <p>
              I built <strong>Mind Body Spirit HYGIENE™ Method</strong> because I needed it first. I needed a daily practice that wasn't just therapy, and wasn't dogma, and wasn't another wellness trend I'd abandon in six weeks. I needed real tools, for real women, in the messy middle. I needed permission to go slow, guided, held — because I had been in fight-and-flight for so many, many years I truly didn't know what I didn't know. I had to relearn, reclaim how to understand, manage, forgive, and love my whole operating system — inside and out. Mind. Body. Spirit.
            </p>
            <p>So please — hear me carefully:</p>
            <p className="font-serif italic text-2xl text-[#5EEAD4] py-2">
              Do not make any BIG choices from trauma, fight, or flight. Start small. Get help where needed.
            </p>
            <p>
              My gift to you: join the <strong>free 52-week journey home</strong> — one letter a week, from my kitchen table to yours. No urgency. Just a space to get started.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link
              to="/yes-no-wait"
              className="inline-flex items-center justify-center gap-2 bg-[#5EEAD4] hover:bg-white text-[#042F2E] font-bold rounded-full px-6 py-3.5 text-sm tracking-wider uppercase transition"
              data-testid="cta-cards"
            >
              <Heart className="w-4 h-4" /> Get the free cards
            </Link>
            <a
              href="/workbook?from=about"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-full px-6 py-3.5 text-sm tracking-wider uppercase transition"
              data-testid="cta-journey"
            >
              <Mail className="w-4 h-4" /> Start the 52-week journey
            </a>
          </div>

          <p className="font-serif italic text-[#5EEAD4] text-lg mt-10">
            There is a candle in the window of your own soul.
          </p>
          <p className="font-serif italic text-white/70 mt-1 text-lg">— Welcome home.</p>
          <p className="font-serif text-white text-base mt-6">— Temple Franklin</p>
          <p className="font-serif italic text-[#5EEAD4] text-sm mt-1" data-testid="signature-frame">
            Initiated. Rebuilt. Sovereign.
          </p>
          <p className="font-serif italic text-white/80 text-sm">And in the Messy Middle.</p>
          <p className="text-white/60 text-xs uppercase tracking-widest mt-4">Mind Body Spirit HYGIENE™ Method</p>

          {/* JOURNEY SERIES — cross-link to "Temple's Journey" blog series */}
          <div className="mt-12 pt-10 border-t border-white/15 text-left max-w-xl mx-auto">
            <p className="font-sans text-xs tracking-[0.32em] uppercase font-semibold mb-3 text-[#7BC8CB]">
              Temple's Journey · the long story
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-3 leading-snug">
              The pieces of the story — told one chapter at a time.
            </h3>
            <p className="font-sans text-white/75 text-sm mb-5 leading-relaxed">
              A new series of essays — what it actually looked like to be brought down to the studs, and what I&rsquo;m still learning on the other side.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <a
                href="/blog/the-only-one-rowing"
                data-testid="about-journey-rowing-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-sans font-semibold text-sm transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: "#F4F1EC", color: "#1B6B6E" }}
              >
                #1 The Only One Rowing →
              </a>
              <a
                href="/blog/the-losses-no-one-brings-a-casserole-for"
                data-testid="about-journey-casserole-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-sans font-semibold text-sm transition-all hover:-translate-y-0.5 border-2"
                style={{ borderColor: "#7BC8CB", color: "#F4F1EC" }}
              >
                #2 The Losses No One Brings a Casserole For →
              </a>
              <a
                href="/blog/grieving-people-who-are-still-here"
                data-testid="about-journey-grieving-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-sans font-semibold text-sm transition-all hover:-translate-y-0.5 border-2"
                style={{ borderColor: "#7BC8CB", color: "#F4F1EC" }}
              >
                #3 Grieving People Who Are Still Here →
              </a>
              <a
                href="/blog/coming-home-to-myself"
                data-testid="about-journey-coming-home-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-sans font-semibold text-sm transition-all hover:-translate-y-0.5 border-2"
                style={{ borderColor: "#7BC8CB", color: "#F4F1EC" }}
              >
                #4 Coming Home to Myself →
              </a>
              <a
                href="/blog/when-your-spirit-stays-high-and-your-heart-is-breaking"
                data-testid="about-journey-spirit-heart-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-sans font-semibold text-sm transition-all hover:-translate-y-0.5 border-2"
                style={{ borderColor: "#7BC8CB", color: "#F4F1EC" }}
              >
                #5 When Your Spirit Stays High and Your Heart Is Breaking →
              </a>
              <a
                href="/blog/i-was-told-not-to-air-our-dirty-laundry"
                data-testid="about-journey-dirty-laundry-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-sans font-semibold text-sm transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: "#F4F1EC", color: "#1B6B6E" }}
              >
                #6 I Was Told Not to Air Our Dirty Laundry →
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
